import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import Map, { Source, Layer, NavigationControl } from "react-map-gl/mapbox";
import Box from "@mui/material/Box";

import SideMenu from "./sideMenu";
import MapMenu from "./mapMenu";
import {
  MAP_STYLE,
  floodHazardLayer,
  FLOOD_HAZARD_TILESETS,
  buildFloodHazardFillColor,
  floodRiversLayer,
  floodLakesLayer,
  MAPBOX_TILES,
  EARTHQUAKE_HAZARD_SOURCE_URL,
  EARTHQUAKE_TECTONIC_SOURCE_URL,
  EARTHQUAKE_FAULTS_SOURCE_URL,
  earthquakeHazardFillLayer,
  earthquakeTectonicPlatesLayer,
  earthquakeActiveFaultsLayer,
  FIRE_TILES_HIGH_ZOOM,
  fireGenericLayer,
  FIRE_TILES_LOW_ZOOM,
  HISTORICAL_HAZARD_CONFIG,
  historicalFloodLayer,
  historicalFloodLoaderLayer,
  historicalFloodCountLayer,
  historicalFireFillLayer,
} from "./constants";
import { useHistoricalFloodEvents } from "./useHistoricalFloodEvents";
import Marker from "./marker";
import CoordinatesPopup from "./coordinatesPopup";
import MapSearch from "../shared/mapSearch";
import useSidebarWidth from "../../shared/hooks/useSidebarWidth";
import { MapLayerOrderEnforcer } from "../shared/mapLayerOrder";
import { useLayerOpacities } from "../shared/useLayerOpacities";
import { useShow3dObjects } from "../shared/useShow3dObjects";
import {
  getNearbyWaterFeature,
  getWaterFeatureName,
  getHistoricalFeatureAtPoint,
  getHistoricalFeaturesAtPoint,
} from "./helpers";
import CircleSource from "./circleSource";
import WaterFeaturePopup from "./waterFeaturePopup";
import HistoricalHazardPopup from "./historicalHazardPopup";
import FloodDistanceMarkers from "./floodDistanceMarkers/floodDistanceMarkers";
import AssessmentHistoryMarkers from "./markers/AssessmentHistoryMarkers/assessmentHistoryMarkers";
import HistoryAreaPolygons from "./markers/HistoryAreaPolygons/historyAreaPolygons";
import FireBufferPolygons from "./markers/FireBufferPolygons/fireBufferPolygons";
import {
  PolygonDrawProvider,
  DrawControl,
  PolygonToolbar,
  PolygonMetricsLabels,
  DrawCursorPopup,
  usePolygonDrawContext,
} from "./polygonDraw";

import "mapbox-gl/dist/mapbox-gl.css";

const initialViewState = {
  longitude: 23.7348324,
  latitude: 37.9755648,
  zoom: 10,
};

function RisksMap({
  mapRef,
  markerInfo,
  setMarkerInfo,
  setFormValues,
  activeSideMenuTab,
  setActiveSideMenuTab,
  historyAssessmentsForMap,
  selectedEarthquakeLayers,
  setSelectedEarthquakeLayers,
  selectedFloodLayers,
  setSelectedFloodLayers,
  selectedAreaAssessment,
  setSelectedAreaAssessment,
}) {
  const { results: areaResult } = usePolygonDrawContext();

  const [activeRisk, setActiveRisk] = useState("fire");
  const [activeMapStyle, setActiveMapStyle] = useState(MAP_STYLE.street);
  const [rightClickCoords, setRightClickCoords] = useState(null);
  const [show3DMap, setShow3DMap] = useState(false);
  // 3D buildings/landmarks/trees are shown by default in the Mapbox Standard styles.
  const [show3DBuildings, setShow3DBuildings] = useState(true);
  const [hoveredWaterFeature, setHoveredWaterFeature] = useState(null);
  const [showHistoricalHazards, setShowHistoricalHazards] = useState(false);
  const [hoveredHistorical, setHoveredHistorical] = useState(null);
  // Feature-state `hover` ids currently applied to fire historical polygons, so
  // we can clear the ones no longer under the cursor without re-querying.
  const hoveredFireIdsRef = useRef([]);
  const { layerOpacities, setLayerOpacity } = useLayerOpacities();

  // Historical overlay config for the active hazard, only when the toggle is on
  // and a tileset URL is actually configured for it.
  const historicalConfig = HISTORICAL_HAZARD_CONFIG[activeRisk];
  const historicalActive = Boolean(
    showHistoricalHazards && historicalConfig?.url,
  );
  const historicalLayerIds = useMemo(
    () =>
      historicalActive
        ? historicalConfig.layers.map((layer) => layer.id)
        : [],
    [historicalActive, historicalConfig],
  );

  // Flood historical points render from a grouped GeoJSON mirror of the
  // tileset (one feature per location) so same-spot events don't stack
  // opacity and hover can list all of them.
  const historicalFloodActive = historicalActive && activeRisk === "flood";
  const historicalFloodEvents = useHistoricalFloodEvents({
    mapRef,
    enabled: historicalFloodActive,
    sourceId: "historical-flood",
    sourceLayer: HISTORICAL_HAZARD_CONFIG.flood.sourceLayer,
  });

  // Satellite (Standard Satellite) has no 3D buildings, so force them off there
  // while keeping the user's toggle preference for when they return to street.
  useShow3dObjects(
    mapRef,
    show3DBuildings && activeMapStyle !== MAP_STYLE.satellite,
  );

  // Clear the `hover` feature-state from every fire polygon we last highlighted.
  const clearFireHover = useCallback((map) => {
    if (hoveredFireIdsRef.current.length === 0) {
      return;
    }
    if (map) {
      const sourceLayer = HISTORICAL_HAZARD_CONFIG.fire.sourceLayer;
      for (const id of hoveredFireIdsRef.current) {
        map.setFeatureState(
          { source: "historical-fire", sourceLayer, id },
          { hover: false },
        );
      }
    }
    hoveredFireIdsRef.current = [];
  }, []);

  const onMapClick = useCallback(
    (e) => {
      // While drawing polygons, map clicks belong to mapbox-gl-draw — don't
      // open the coordinates popup.
      if (activeSideMenuTab === "polygon") {
        return;
      }

      if (e?.originalEvent?.target?.closest?.('[data-history-marker="true"]')) {
        return;
      }

      // Clicks on the assessment marker are handled by the DOM marker itself
      // (it stops propagation), so any click reaching the map sets coordinates.
      const { lng, lat } = e.lngLat;
      setRightClickCoords({ lat, lng });
    },
    [activeSideMenuTab],
  );

  const onLocationSelect = useCallback(
    ({ lat, lng }) => {
      mapRef.current?.getMap()?.flyTo({ center: [lng, lat] });
      setRightClickCoords({ lat, lng });
    },
    [mapRef],
  );

  const onMapMouseMove = useCallback(
    (event) => {
      const map = mapRef.current?.getMap();

      // Historical points/polygons sit above the hazard fills, so check them
      // first regardless of hazard type.
      if (historicalActive && map) {
        // Fire: highlight every polygon under the cursor (darken via
        // feature-state hover) and, when several overlap, list them all.
        if (activeRisk === "fire") {
          const fireFeatures = getHistoricalFeaturesAtPoint({
            map,
            point: event.point,
            layerId: historicalFireFillLayer.id,
          });

          if (fireFeatures.length > 0) {
            const sourceLayer = HISTORICAL_HAZARD_CONFIG.fire.sourceLayer;
            const nextIds = fireFeatures
              .map((feature) => feature.id)
              .filter((id) => id !== undefined && id !== null);

            // Clear polygons no longer hovered, then highlight the new ones.
            for (const id of hoveredFireIdsRef.current) {
              if (!nextIds.includes(id)) {
                map.setFeatureState(
                  { source: "historical-fire", sourceLayer, id },
                  { hover: false },
                );
              }
            }
            for (const id of nextIds) {
              map.setFeatureState(
                { source: "historical-fire", sourceLayer, id },
                { hover: true },
              );
            }
            hoveredFireIdsRef.current = nextIds;

            map.getCanvas().style.cursor = "pointer";
            setHoveredHistorical({
              longitude: event.lngLat.lng,
              latitude: event.lngLat.lat,
              properties: fireFeatures[0].properties,
              events: fireFeatures.map((feature) => feature.properties),
            });
            if (hoveredWaterFeature) {
              setHoveredWaterFeature(null);
            }
            return;
          }

          clearFireHover(map);
          map.getCanvas().style.cursor = "";
          if (hoveredHistorical) {
            setHoveredHistorical(null);
          }
          return;
        }

        const historicalFeature = getHistoricalFeatureAtPoint({
          map,
          point: event.point,
          layerIds: historicalLayerIds,
        });

        if (historicalFeature) {
          map.getCanvas().style.cursor = "pointer";
          setHoveredHistorical({
            longitude: event.lngLat.lng,
            latitude: event.lngLat.lat,
            properties: historicalFeature.properties,
          });
          if (hoveredWaterFeature) {
            setHoveredWaterFeature(null);
          }
          return;
        }

        if (hoveredHistorical) {
          setHoveredHistorical(null);
        }
      } else if (hoveredHistorical) {
        setHoveredHistorical(null);
      }

      if (activeRisk !== "flood") {
        if (hoveredWaterFeature) {
          setHoveredWaterFeature(null);
        }
        if (map) {
          map.getCanvas().style.cursor = "";
        }
        return;
      }

      if (!map) {
        return;
      }

      const nearbyFeature = getNearbyWaterFeature({
        map,
        point: event.point,
        activeRisk,
        riversLayerId: floodRiversLayer.id,
        lakesLayerId: floodLakesLayer.id,
        disableRivers: true,
      });

      if (!nearbyFeature) {
        if (hoveredWaterFeature) {
          setHoveredWaterFeature(null);
        }
        map.getCanvas().style.cursor = "";
        return;
      }

      map.getCanvas().style.cursor = "pointer";
      setHoveredWaterFeature({
        name: getWaterFeatureName(nearbyFeature),
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat,
      });
    },
    [
      activeRisk,
      hoveredWaterFeature,
      hoveredHistorical,
      historicalActive,
      historicalLayerIds,
      clearFireHover,
      mapRef,
    ],
  );

  const onMapMouseLeave = useCallback(() => {
    if (hoveredWaterFeature) {
      setHoveredWaterFeature(null);
    }
    if (hoveredHistorical) {
      setHoveredHistorical(null);
    }
    const map = mapRef.current?.getMap();
    clearFireHover(map);
    if (map) {
      map.getCanvas().style.cursor = "";
    }
  }, [hoveredWaterFeature, hoveredHistorical, clearFireHover, mapRef]);

  const interactiveLayerIds = useMemo(() => {
    const ids = [];
    if (activeRisk === "flood") {
      ids.push(floodRiversLayer.id, floodLakesLayer.id);
    }
    ids.push(...historicalLayerIds);

    return ids;
  }, [activeRisk, historicalLayerIds]);

  useEffect(() => {
    // Clear transient map popups/overlays when switching side-menu tabs.
    setRightClickCoords(null);
    setHoveredWaterFeature(null);
    setHoveredHistorical(null);
    clearFireHover(mapRef.current?.getMap());
    setMarkerInfo(null);
    setSelectedAreaAssessment(null);
  }, [
    activeSideMenuTab,
    clearFireHover,
    mapRef,
    setMarkerInfo,
    setSelectedAreaAssessment,
  ]);

  useEffect(() => {
    // Drop any fire highlight/popup when fire's historical overlay isn't active.
    if (activeRisk !== "fire" || !historicalActive) {
      clearFireHover(mapRef.current?.getMap());
      setHoveredHistorical(null);
    }
  }, [activeRisk, historicalActive, clearFireHover, mapRef]);

  const showEarthquakeHazardLayer =
    activeRisk === "earthquake" && selectedEarthquakeLayers.includes("hazard");
  const showEarthquakeTectonicLayer =
    activeRisk === "earthquake" &&
    selectedEarthquakeLayers.includes("tectonicPlates");
  const showEarthquakeFaultsLayer =
    activeRisk === "earthquake" &&
    selectedEarthquakeLayers.includes("activeFaults");

  const showFloodHazardLayer =
    activeRisk === "flood" && selectedFloodLayers.includes("hazard");
  const showFloodRiversLayer =
    activeRisk === "flood" && selectedFloodLayers.includes("rivers");
  const showFloodLakesLayer =
    activeRisk === "flood" && selectedFloodLayers.includes("lakes");

  const selectedHistoryAssessmentId =
    markerInfo?.assessmentId || markerInfo?.riskData?.id;

  return (
    <div
      style={{
        width: "100%",
        height: "calc(100vh - 64px)",
        position: "relative",
      }}
    >
      <MapSearch
        onLocationSelect={onLocationSelect}
        placeholderKey="risksMap.mapSearchPlaceholder"
      />

      <Map
        ref={mapRef}
        style={{ width: "100%", height: "100%" }}
        mapStyle={activeMapStyle}
        projection="mercator"
        terrain={
          show3DMap ? { source: "mapbox-dem", exaggeration: 1.2 } : undefined
        }
        initialViewState={initialViewState}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        onClick={onMapClick}
        onMouseMove={onMapMouseMove}
        onMouseLeave={onMapMouseLeave}
        interactiveLayerIds={interactiveLayerIds}
        maxPitch={85}
      >
        <NavigationControl position="bottom-right" />

        <DrawControl />
        <PolygonMetricsLabels />
        <DrawCursorPopup />

        <MapLayerOrderEnforcer />

        {/* --- 3D TERRAIN DEM SOURCE --- */}
        <Source
          id="mapbox-dem"
          type="raster-dem"
          url="mapbox://mapbox.mapbox-terrain-dem-v1"
          tileSize={512}
          maxzoom={14}
        />

        {rightClickCoords && (
          <CoordinatesPopup
            rightClickCoords={rightClickCoords}
            setRightClickCoords={setRightClickCoords}
            onAdd={(coords) => {
              setActiveSideMenuTab("assessment");
              setFormValues((prev) => ({
                ...prev,
                ...coords,
                place: null,
                activeAddressType: "coordinates",
                showResults: false,
              }));
            }}
          />
        )}

        {activeSideMenuTab === "history" && (
          <AssessmentHistoryMarkers
            assessments={historyAssessmentsForMap}
            mapRef={mapRef}
            selectedAssessmentId={selectedHistoryAssessmentId}
            setMarkerInfo={setMarkerInfo}
          />
        )}

        {activeSideMenuTab === "history" && selectedAreaAssessment && (
          <HistoryAreaPolygons geometry={selectedAreaAssessment.geometry} />
        )}

        {activeSideMenuTab === "history" &&
          selectedAreaAssessment &&
          activeRisk === "fire" && (
            <FireBufferPolygons fire={selectedAreaAssessment.fire} />
          )}

        {activeSideMenuTab === "polygon" &&
          areaResult &&
          activeRisk === "fire" && <FireBufferPolygons fire={areaResult.fire} />}

        {markerInfo &&
          (activeSideMenuTab === "assessment" ? (
            <Marker markerInfo={markerInfo} setMarkerInfo={setMarkerInfo} />
          ) : (
            <Marker
              markerInfo={markerInfo}
              setMarkerInfo={setMarkerInfo}
              showPoint={false}
            />
          ))}

        {activeRisk === "flood" && (
          <WaterFeaturePopup feature={hoveredWaterFeature} />
        )}

        {markerInfo && activeRisk === "flood" && (
          <FloodDistanceMarkers markerInfo={markerInfo} />
        )}

        {markerInfo && activeRisk === "fire" && (
          <CircleSource
            center={[
              markerInfo.riskData.longitude,
              markerInfo.riskData.latitude,
            ]}
          />
        )}

        {activeRisk === "fire" && (
          <>
            {FIRE_TILES_HIGH_ZOOM.map((tileset, index) => {
              return (
                <Source
                  key={index}
                  id={`fire-${index}`}
                  type="vector"
                  url={tileset.url}
                >
                  <Layer
                    {...fireGenericLayer}
                    id={`fire-layer-${index}`}
                    source-layer={tileset["source-layer"]}
                    minzoom={11}
                    paint={{
                      ...fireGenericLayer.paint,
                      "fill-opacity": layerOpacities.fire,
                    }}
                  />
                </Source>
              );
            })}

            {FIRE_TILES_LOW_ZOOM.map((tileset, index) => {
              return (
                <Source
                  key={index}
                  id={`fire-low-${index}`}
                  type="vector"
                  url={tileset.url}
                >
                  <Layer
                    {...fireGenericLayer}
                    id={`fire-low-layer-${index}`}
                    source-layer={tileset["source-layer"]}
                    maxzoom={11}
                    paint={{
                      ...fireGenericLayer.paint,
                      "fill-opacity": layerOpacities.fire,
                    }}
                  />
                </Source>
              );
            })}
          </>
        )}

        {activeRisk === "flood" && (
          <>
            {showFloodHazardLayer &&
              FLOOD_HAZARD_TILESETS.map((tileset) => (
                <Source
                  key={tileset.id}
                  id={tileset.id}
                  type="vector"
                  url={tileset.url}
                >
                  <Layer
                    {...floodHazardLayer}
                    id={`${tileset.id}-layer`}
                    minzoom={tileset.minzoom}
                    maxzoom={tileset.maxzoom}
                    paint={{
                      ...floodHazardLayer.paint,
                      "fill-color": buildFloodHazardFillColor(
                        tileset.property,
                        tileset.colorStops,
                      ),
                      "fill-opacity": layerOpacities.flood,
                      ...(tileset.useTransparentOutline
                        ? { "fill-outline-color": "rgba(0, 0, 0, 0)" }
                        : {}),
                    }}
                  />
                </Source>
              ))}

            {showFloodRiversLayer && (
              <Source id="flood-rivers" type="vector" url={MAPBOX_TILES.rivers}>
                <Layer
                  {...floodRiversLayer}
                  paint={{
                    ...floodRiversLayer.paint,
                    "line-opacity": layerOpacities.flood,
                  }}
                />
              </Source>
            )}

            {showFloodLakesLayer && (
              <Source id="flood-lakes" type="vector" url={MAPBOX_TILES.lakes}>
                <Layer
                  {...floodLakesLayer}
                  paint={{
                    ...floodLakesLayer.paint,
                    "fill-opacity": layerOpacities.flood,
                  }}
                />
              </Source>
            )}
          </>
        )}

        {showEarthquakeTectonicLayer && (
          <Source
            id="earthquake-tectonic-plates"
            type="vector"
            url={EARTHQUAKE_TECTONIC_SOURCE_URL}
          >
            <Layer
              {...earthquakeTectonicPlatesLayer}
              paint={{
                ...earthquakeTectonicPlatesLayer.paint,
                "line-opacity": layerOpacities.earthquake,
              }}
            />
          </Source>
        )}

        {showEarthquakeFaultsLayer && (
          <Source
            id="earthquake-active-faults"
            type="vector"
            url={EARTHQUAKE_FAULTS_SOURCE_URL}
          >
            <Layer
              {...earthquakeActiveFaultsLayer}
              paint={{
                ...earthquakeActiveFaultsLayer.paint,
                "line-opacity": layerOpacities.earthquake,
              }}
            />
          </Source>
        )}

        {showEarthquakeHazardLayer && (
          <Source
            id="earthquake-risk"
            type="vector"
            url={EARTHQUAKE_HAZARD_SOURCE_URL}
          >
            <Layer
              {...earthquakeHazardFillLayer}
              paint={{
                ...earthquakeHazardFillLayer.paint,
                "fill-opacity": layerOpacities.earthquake,
              }}
            />
          </Source>
        )}

        {/* --- HISTORICAL (PAST-EVENT) OVERLAY FOR THE ACTIVE HAZARD --- */}
        {historicalActive &&
          (historicalFloodActive ? (
            <>
              {/* Invisible loader keeps the flood tileset's tiles loading so
                  useHistoricalFloodEvents can read and regroup them. */}
              <Source
                id="historical-flood"
                type="vector"
                url={historicalConfig.url}
              >
                <Layer
                  {...historicalFloodLoaderLayer}
                  source-layer={historicalConfig.sourceLayer}
                />
              </Source>
              <Source
                id="historical-flood-grouped"
                type="geojson"
                data={historicalFloodEvents}
              >
                <Layer {...historicalFloodLayer} />
                <Layer {...historicalFloodCountLayer} />
              </Source>
            </>
          ) : (
            <Source
              key={`historical-${activeRisk}`}
              id={`historical-${activeRisk}`}
              type="vector"
              url={historicalConfig.url}
              {...(activeRisk === "fire"
                ? { promoteId: { [historicalConfig.sourceLayer]: "id" } }
                : {})}
            >
              {historicalConfig.layers.map((layer) => (
                <Layer
                  key={layer.id}
                  {...layer}
                  source-layer={historicalConfig.sourceLayer}
                />
              ))}
            </Source>
          ))}

        {historicalActive && (
          <HistoricalHazardPopup
            activeRisk={activeRisk}
            feature={hoveredHistorical}
          />
        )}
      </Map>

      <MapMenu
        setActiveRisk={setActiveRisk}
        activeRisk={activeRisk}
        setActiveMapStyle={setActiveMapStyle}
        activeMapStyle={activeMapStyle}
        show3DMap={show3DMap}
        setShow3DMap={setShow3DMap}
        show3DBuildings={show3DBuildings}
        setShow3DBuildings={setShow3DBuildings}
        selectedEarthquakeLayers={selectedEarthquakeLayers}
        setSelectedEarthquakeLayers={setSelectedEarthquakeLayers}
        selectedFloodLayers={selectedFloodLayers}
        setSelectedFloodLayers={setSelectedFloodLayers}
        layerOpacities={layerOpacities}
        setLayerOpacity={setLayerOpacity}
        showHistoricalHazards={showHistoricalHazards}
        setShowHistoricalHazards={setShowHistoricalHazards}
      />

      <PolygonToolbar />
    </div>
  );
}

export default function Container() {
  const mapRef = useRef();

  const { width: drawerWidth, setWidth: setDrawerWidth } = useSidebarWidth({
    storageKey: "artemis.sidebarWidth.risksMap",
    minWidth: 440,
  });

  const [menuOpen, setMenuOpen] = React.useState(true);

  const [markerInfo, setMarkerInfo] = useState(null);
  const [activeSideMenuTab, setActiveSideMenuTab] = useState("assessment");
  const [historyAssessmentsForMap, setHistoryAssessmentsForMap] = useState([]);
  const [selectedEarthquakeLayers, setSelectedEarthquakeLayers] = useState([
    "hazard",
  ]);
  const [selectedFloodLayers, setSelectedFloodLayers] = useState(["hazard"]);
  const [selectedAreaAssessment, setSelectedAreaAssessment] = useState(null);

  const [formValues, setFormValues] = useState({
    place: null,
    floor: "ΙΣΟΓΕΙΟ",
    latitude: "",
    longitude: "",
    egsa87x: "",
    egsa87y: "",
    name: "",
    notes: "",
    activeAddressType: "address",
    showResults: false,
  });

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  useEffect(() => {
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.getMap().resize();
      }
    }, 300); // matches your transition duration
  }, [menuOpen, drawerWidth]);

  return (
    <PolygonDrawProvider>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            width: { sm: `calc(100% - ${menuOpen ? drawerWidth : 0}px)` },
            transition: "margin 0.3s",
            marginRight: menuOpen ? `${drawerWidth}px` : 0,
          }}
        >
          <RisksMap
            mapRef={mapRef}
            markerInfo={markerInfo}
            setMarkerInfo={setMarkerInfo}
            setFormValues={setFormValues}
            activeSideMenuTab={activeSideMenuTab}
            setActiveSideMenuTab={setActiveSideMenuTab}
            historyAssessmentsForMap={historyAssessmentsForMap}
            selectedEarthquakeLayers={selectedEarthquakeLayers}
            setSelectedEarthquakeLayers={setSelectedEarthquakeLayers}
            selectedFloodLayers={selectedFloodLayers}
            setSelectedFloodLayers={setSelectedFloodLayers}
            selectedAreaAssessment={selectedAreaAssessment}
            setSelectedAreaAssessment={setSelectedAreaAssessment}
          />
        </Box>

        <SideMenu
          open={menuOpen}
          handleToggle={handleMenuToggle}
          mapRef={mapRef}
          setMarkerInfo={setMarkerInfo}
          formValues={formValues}
          setFormValues={setFormValues}
          activeTab={activeSideMenuTab}
          setActiveTab={setActiveSideMenuTab}
          setHistoryAssessmentsForMap={setHistoryAssessmentsForMap}
          selectedEarthquakeLayers={selectedEarthquakeLayers}
          setSelectedAreaAssessment={setSelectedAreaAssessment}
          width={drawerWidth}
          onResize={setDrawerWidth}
        />
      </Box>
    </PolygonDrawProvider>
  );
}
